/**
 * Returns Shipping Form Handler
 * Handles 3-step form, validation, API integration, and local storage
 */

class ReturnsShippingForm {
  constructor(formElement) {
    this.form = formElement;
    this.currentStep = 1;
    this.formData = {};

    this.init();
  }

  init() {
    this.attachEventListeners();
    this.setupMaterialCheckboxes();
    this.setupTotalWeightCalculator();
    this.setupDesiCalculator();
    this.loadSavedData();
    this.updateProgressBar();

    // Initialize calculations on page load
    this.updateTotalWeight();
    this.calculateDesi();
  }

  attachEventListeners() {
    // Next buttons - MOBİL DESTEK ile
    const nextButtons = document.querySelectorAll('.returns-next-btn');

    nextButtons.forEach(btn => {
      let touchHandled = false;

      // Click handler
      const clickHandler = (e) => {
        e.preventDefault();

        // Prevent double-firing on mobile
        if (e.type === 'click' && touchHandled) {
          touchHandled = false;
          return;
        }

        // Button loading state
        if (btn.classList.contains('loading')) {
          return; // Already processing
        }

        const nextStep = parseInt(e.currentTarget.dataset.nextStep);

        // Add loading state
        btn.classList.add('loading');
        btn.disabled = true;

        // Small delay to ensure smooth UI
        setTimeout(() => {
          if (this.validateCurrentStep()) {
            this.goToStep(nextStep);
          }

          // Remove loading state
          btn.classList.remove('loading');
          btn.disabled = false;
        }, 50);
      };

      // Touch handler for mobile
      const touchHandler = (e) => {
        touchHandled = true;
        clickHandler(e);

        // Reset flag after a short delay
        setTimeout(() => {
          touchHandled = false;
        }, 500);
      };

      btn.addEventListener('click', clickHandler);
      btn.addEventListener('touchend', touchHandler, { passive: false });
    });

    // Terms link - Open modal
    const termsLink = document.querySelector('.returns-terms-link');
    if (termsLink) {
      termsLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent checkbox toggle
        this.openTermsModal();
      });
    }

    // Previous buttons
    document.querySelectorAll('.returns-prev-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const prevStep = parseInt(e.currentTarget.dataset.prevStep);
        this.goToStep(prevStep);
      });
    });

    // Form submission - now using click on div instead of form submit
    const createLabelBtn = document.getElementById('create-label-btn');
    if (createLabelBtn) {
      createLabelBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }

    // Auto-save on input change
    this.form.addEventListener('input', () => {
      this.saveFormData();
    });

    // Remove invalid state on focus
    this.form.addEventListener('focus', (e) => {
      if (e.target.matches('input, textarea, select')) {
        e.target.classList.remove('invalid');
      }
    }, true);

    // Phone number formatting
    const phoneInput = document.getElementById('sender-phone');
    if (phoneInput) {
      phoneInput.addEventListener('input', this.formatPhoneNumber);
    }
  }

  setupDesiCalculator() {
    const lengthInput = document.getElementById('package-length');
    const widthInput = document.getElementById('package-width');
    const heightInput = document.getElementById('package-height');

    [lengthInput, widthInput, heightInput].forEach(input => {
      if (input) {
        input.addEventListener('input', () => this.calculateDesi());
      }
    });
  }

  calculateDesi() {
    const lengthInput = document.getElementById('package-length');
    const widthInput = document.getElementById('package-width');
    const heightInput = document.getElementById('package-height');
    const dimensionalDesiDisplay = document.getElementById('dimensional-desi');
    const weightDesiDisplay = document.getElementById('weight-desi');
    const desiValue = document.getElementById('desi-value');
    const desiWarning = document.getElementById('desi-warning');
    const desiCalculator = document.getElementById('desi-calculator');

    // Calculate dimensional desi (volume-based)
    const length = parseFloat(lengthInput?.value) || 0;
    const width = parseFloat(widthInput?.value) || 0;
    const height = parseFloat(heightInput?.value) || 0;
    const dimensionalDesi = (length * width * height) / 3000;

    // Calculate weight desi (weight-based)
    const totalWeight = this.totalWeight || 0;
    const weightDesi = totalWeight;

    // Final desi is the maximum of both
    const finalDesi = Math.max(dimensionalDesi, weightDesi);

    // Update hidden displays (for API payload)
    if (dimensionalDesiDisplay) {
      dimensionalDesiDisplay.textContent = dimensionalDesi.toFixed(2);
    }
    if (weightDesiDisplay) {
      weightDesiDisplay.textContent = weightDesi.toFixed(2);
    }
    if (desiValue) {
      desiValue.textContent = finalDesi.toFixed(2);
    }

    // Color coding for desi value
    if (desiValue && desiCalculator) {
      if (finalDesi === 0) {
        // No value entered yet - gray
        desiValue.style.color = '#999';
        desiCalculator.style.borderColor = '#ddd';
      } else if (finalDesi < 2) {
        // Too low - warning (yellow/orange)
        desiValue.style.color = '#ff9800';
        desiCalculator.style.borderColor = '#ff9800';
      } else if (finalDesi > 32) {
        // Too high - error (red)
        desiValue.style.color = '#dc3545';
        desiCalculator.style.borderColor = '#dc3545';
      } else {
        // Good range - success (blue)
        desiValue.style.color = '#0066cc';
        desiCalculator.style.borderColor = '#0066cc';
      }
    }

    // Show warning if over 32 desi
    if (desiWarning) {
      if (finalDesi > 32) {
        desiWarning.style.display = 'block';
        return false;
      } else {
        desiWarning.style.display = 'none';
        return true;
      }
    }

    return finalDesi <= 32;
  }

  formatPhoneNumber(e) {
    let value = e.target.value.replace(/\D/g, '');

    // Turkish phone format: 05XX XXX XX XX
    if (value.length > 0) {
      if (value.length <= 4) {
        value = value;
      } else if (value.length <= 7) {
        value = value.slice(0, 4) + ' ' + value.slice(4);
      } else if (value.length <= 9) {
        value = value.slice(0, 4) + ' ' + value.slice(4, 7) + ' ' + value.slice(7);
      } else {
        value = value.slice(0, 4) + ' ' + value.slice(4, 7) + ' ' + value.slice(7, 9) + ' ' + value.slice(9, 11);
      }
    }

    e.target.value = value;
  }

  setupMaterialCheckboxes() {
    const checkboxes = document.querySelectorAll('.material-checkbox');

    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const materialOption = e.target.closest('.material-option');
        const inputContainer = materialOption.querySelector('.material-input-container');
        const materialLabel = materialOption.querySelector('label');
        const weightInput = inputContainer.querySelector('.material-weight-input, .material-count-input');

        if (e.target.checked) {
          // Show input container
          inputContainer.style.display = 'block';
          materialLabel.style.borderColor = 'var(--color-button-background, #000)';
          materialLabel.style.background = '#f0f8ff';

          // Make input required
          if (weightInput) {
            weightInput.required = true;
          }
        } else {
          // Hide input container
          inputContainer.style.display = 'none';
          materialLabel.style.borderColor = 'transparent';
          materialLabel.style.background = '#f9f9f9';

          // Remove required and clear value
          if (weightInput) {
            weightInput.required = false;
            weightInput.value = '';
          }

          // Update total weight
          this.updateTotalWeight();
        }

        // Boyut alanını göster/gizle
        this.toggleDimensionsSection();
      });
    });
  }

  toggleDimensionsSection() {
    const dimensionFields = document.querySelectorAll('.dimensions-field');
    if (!dimensionFields.length) return;

    const hasWeightMaterial = document.querySelector('.material-checkbox[data-material-type="PLA"]:checked, .material-checkbox[data-material-type="PETG"]:checked');
    const hasMakara = document.querySelector('.material-checkbox[data-material-type="Makara"]:checked');
    const weightHint = document.getElementById('weight-hint');

    // Sadece Makara seçili ve PLA/PETG seçili değilse gizle
    if (hasMakara && !hasWeightMaterial) {
      dimensionFields.forEach(field => {
        field.style.display = 'none';
        const input = field.querySelector('input');
        if (input) input.required = false;
      });
    } else {
      dimensionFields.forEach(field => {
        field.style.display = '';
        const input = field.querySelector('input');
        if (input) input.required = true;
      });
    }

    // Ağırlık ipucu metni
    if (weightHint) {
      weightHint.textContent = hasMakara ? 'Makara 200gr olarak hesaplanır' : 'Minimum 2 kg olmalıdır';
    }
  }

  setupTotalWeightCalculator() {
    const weightInputs = document.querySelectorAll('.material-weight-input');
    const countInputs = document.querySelectorAll('.material-count-input');

    weightInputs.forEach(input => {
      input.addEventListener('input', () => {
        this.updateTotalWeight();
      });
    });

    countInputs.forEach(input => {
      input.addEventListener('input', () => {
        this.updateTotalWeight();
      });
    });
  }

  updateTotalWeight() {
    const weightInputs = document.querySelectorAll('.material-weight-input');
    const countInputs = document.querySelectorAll('.material-count-input');
    let totalWeight = 0;

    weightInputs.forEach(input => {
      const checkbox = input.closest('.material-option').querySelector('.material-checkbox');
      if (checkbox && checkbox.checked && input.value) {
        totalWeight += parseFloat(input.value) || 0;
      }
    });

    // Makara: her adet 0.2 kg (200gr) olarak hesaplanır
    countInputs.forEach(input => {
      const checkbox = input.closest('.material-option').querySelector('.material-checkbox');
      if (checkbox && checkbox.checked && input.value) {
        totalWeight += (parseInt(input.value) || 0) * 0.2;
      }
    });

    const totalWeightDisplay = document.getElementById('total-weight-value');
    const totalWeightContainer = document.getElementById('total-weight-display');

    if (totalWeightDisplay) {
      totalWeightDisplay.textContent = totalWeight.toFixed(2) + ' kg';

      // Update color based on weight with warning colors
      if (totalWeight === 0) {
        // No weight - gray
        totalWeightDisplay.style.color = '#999';
        if (totalWeightContainer) {
          totalWeightContainer.style.borderColor = '#ddd';
        }
      } else if (totalWeight < 2) {
        // Less than minimum (2kg) - warning (orange/yellow)
        totalWeightDisplay.style.color = '#ff9800';
        if (totalWeightContainer) {
          totalWeightContainer.style.borderColor = '#ff9800';
        }
      } else {
        // Valid weight (2kg or more) - success (blue)
        totalWeightDisplay.style.color = '#0066cc';
        if (totalWeightContainer) {
          totalWeightContainer.style.borderColor = '#0066cc';
        }
      }
    }

    // Store total weight for desi calculation
    this.totalWeight = totalWeight;

    // Recalculate desi when weight changes
    this.calculateDesi();
  }

  validateCurrentStep() {
    const currentStepElement = document.querySelector(`.returns-step[data-step="${this.currentStep}"]`);
    const inputs = currentStepElement.querySelectorAll('input[required], select[required], textarea[required]');
    let isValid = true;
    let errorMessages = [];

    // Validate all required fields
    inputs.forEach(input => {
      let value = input.value;

      // For text inputs, trim whitespace
      if (input.tagName !== 'SELECT') {
        value = value.trim();
      }

      let hasError = false;
      let errorMsg = '';

      // Check if empty
      if (!value || value === '') {
        hasError = true;
        errorMsg = this.getFieldLabel(input) + ' zorunludur';
      }
      // Email format validation
      else if (input.type === 'email' && !this.isValidEmail(value)) {
        hasError = true;
        errorMsg = 'Geçerli bir e-posta adresi giriniz';
      }
      // Phone format validation (Turkish format)
      else if (input.type === 'tel' && !this.isValidPhone(value)) {
        hasError = true;
        errorMsg = 'Geçerli bir telefon numarası giriniz (05XX XXX XX XX)';
      }
      // Postal code validation
      else if (input.name === 'sender_postal' && !/^\d{5}$/.test(value)) {
        hasError = true;
        errorMsg = 'Posta kodu 5 rakamdan oluşmalıdır';
      }

      if (hasError) {
        isValid = false;
        input.classList.add('invalid');
        errorMessages.push(errorMsg);
      } else {
        input.classList.remove('invalid');
      }
    });

    // Step 2 specific validations
    if (this.currentStep === 2) {
      // Check if at least one material is selected
      const selectedMaterials = document.querySelectorAll('.material-checkbox:checked:not([disabled])');

      if (selectedMaterials.length === 0) {
        isValid = false;
        errorMessages.push('En az bir materyal seçmelisiniz (PLA, PETG veya Makara)');
      }

      // Check if all selected materials have weight/count values
      selectedMaterials.forEach(checkbox => {
        const materialOption = checkbox.closest('.material-option');
        const weightInput = materialOption.querySelector('.material-weight-input, .material-count-input');

        if (weightInput && (!weightInput.value || parseFloat(weightInput.value) <= 0)) {
          isValid = false;
          weightInput.classList.add('invalid');
          const materialType = checkbox.dataset.materialType;
          const isCount = weightInput.classList.contains('material-count-input');
          errorMessages.push(materialType + (isCount ? ' için adet girilmelidir' : ' için ağırlık girilmelidir'));
        } else if (weightInput) {
          weightInput.classList.remove('invalid');
        }
      });

      // Check minimum total weight (Makara seçiliyse 2kg kontrolü atlanır)
      const hasMakara = document.querySelector('.material-checkbox[data-material-type="Makara"]:checked');
      const totalWeight = this.totalWeight || 0;
      if (!hasMakara && totalWeight > 0 && totalWeight < 2) {
        isValid = false;
        errorMessages.push('Toplam ağırlık minimum 2 kg olmalıdır (Şu an: ' + totalWeight.toFixed(2) + ' kg)');
      }

      // Check maximum desi
      const desi = parseFloat(document.getElementById('desi-value').textContent);
      if (desi > 32) {
        isValid = false;
        errorMessages.push('Maksimum 32 desi aşılamaz (Şu an: ' + desi.toFixed(2) + ' desi)');
      }
    }

    // Show error notification if there are errors
    if (!isValid && errorMessages.length > 0) {
      const uniqueErrors = [...new Set(errorMessages)];
      this.showError(
        'Lütfen formu eksiksiz doldurun',
        uniqueErrors.slice(0, 5).join('\n')
      );

      // Scroll to first invalid field
      const firstInvalidField = currentStepElement.querySelector('.invalid');
      if (firstInvalidField) {
        setTimeout(() => {
          firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalidField.focus();
        }, 300);
      }
    }

    return isValid;
  }

  getFieldLabel(input) {
    // Try to get label text
    const label = input.parentElement.querySelector('label');
    if (label) {
      return label.textContent.replace('*', '').trim();
    }

    // Fallback to placeholder or name
    return input.placeholder || input.name || 'Bu alan';
  }


  showError(message, details = '') {
    this.showNotification(message, 'error', details);
  }

  showSuccessNotification(message) {
    this.showNotification(message, 'success');
  }

  isValidEmail(email) {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhone(phone) {
    // Turkish phone format validation
    // Accepts: 05XX XXX XX XX or 05XXXXXXXXX
    const cleaned = phone.replace(/\s/g, '');
    const phoneRegex = /^0[5][0-9]{9}$/;
    return phoneRegex.test(cleaned);
  }

  showNotification(message, type = 'error', details = '') {
    // Remove any existing notifications
    const existingNotification = document.querySelector('.returns-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    // Haptic feedback for mobile (if supported)
    if (type === 'error' && 'vibrate' in navigator) {
      navigator.vibrate([50, 30, 50]); // Short vibration pattern
    }

    // Create notification element
    const notification = document.createElement('div');
    notification.className = `returns-notification returns-notification--${type}`;

    const iconSvg = type === 'error'
      ? '<svg style="width: 24px; height: 24px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>'
      : '<svg style="width: 24px; height: 24px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';

    notification.innerHTML = `
      <div style="display: flex; gap: 0.75rem; align-items: start;">
        ${iconSvg}
        <div style="flex: 1;">
          <div style="font-weight: 600; margin-bottom: 0.25rem;">${message}</div>
          ${details ? `<div style="font-size: 0.875rem; opacity: 0.9; white-space: pre-line;">${details}</div>` : ''}
        </div>
        <button class="notification-close" style="background: none; border: none; color: inherit; cursor: pointer; padding: 0; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; opacity: 0.7; transition: opacity 0.2s;">
          <svg style="width: 20px; height: 20px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;

    // Add to page
    document.body.appendChild(notification);

    // Close button
    notification.querySelector('.notification-close').addEventListener('click', () => {
      notification.remove();
    });

    // Auto-remove after 8 seconds for success, 12 seconds for errors
    setTimeout(() => {
      if (notification.parentElement) {
        notification.style.opacity = '0';
        setTimeout(() => notification.remove(), 300);
      }
    }, type === 'success' ? 8000 : 12000);

    // Scroll notification into view
    setTimeout(() => {
      notification.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  }

  goToStep(stepNumber) {
    // Hide all steps
    document.querySelectorAll('.returns-step').forEach(step => {
      step.style.display = 'none';
    });

    // Show target step
    const targetStep = document.querySelector(`.returns-step[data-step="${stepNumber}"]`);
    if (targetStep) {
      targetStep.style.display = 'block';
      this.currentStep = stepNumber;
      this.updateProgressBar();

      // Update summary if going to step 3
      if (stepNumber === 3) {
        this.updateSummary();
      }

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  updateProgressBar() {
    // Update step indicators
    document.querySelectorAll('.returns-step-indicator').forEach((indicator, index) => {
      const stepNumber = index + 1;
      const circle = indicator.querySelector('.step-circle');

      // Remove all state classes first
      indicator.classList.remove('active', 'completed');

      if (stepNumber < this.currentStep) {
        // Completed step - green border, gray background, green text
        circle.style.background = '#e0e0e0';
        circle.style.color = '#28a745';
        circle.style.border = '1px solid #28a745';
        indicator.classList.add('completed');
      } else if (stepNumber === this.currentStep) {
        // Active step - navy border, gray background, navy text
        circle.style.background = '#e0e0e0';
        circle.style.color = '#003d82';
        circle.style.border = '1px solid #003d82';
        indicator.classList.add('active');
      } else {
        // Inactive step - no border, gray background, gray text
        circle.style.background = '#e0e0e0';
        circle.style.color = '#666';
        circle.style.border = 'none';
      }
    });

    // Update progress line
    const progressLine = document.querySelector('.returns-progress-line-active');
    if (progressLine) {
      const progress = ((this.currentStep - 1) / 2) * 100; // 3 steps: 0%, 50%, 100%
      progressLine.style.width = `${progress}%`;
    }
  }

  updateSummary() {
    // Get form data
    const formData = new FormData(this.form);

    // Sender info
    const senderHTML = `
      <div><strong>${formData.get('sender_name')}</strong></div>
      <div>${formData.get('sender_phone')}</div>
      <div>${formData.get('sender_email')}</div>
      <div style="margin-top: 0.5rem;">
        ${formData.get('sender_address')}<br>
        ${formData.get('sender_district')}, ${formData.get('sender_city')} ${formData.get('sender_postal')}
      </div>
    `;
    document.getElementById('summary-sender').innerHTML = senderHTML;

    // Collect selected materials
    const selectedMaterials = [];
    const checkboxes = document.querySelectorAll('.material-checkbox:checked:not([disabled])');

    checkboxes.forEach(checkbox => {
      const materialType = checkbox.dataset.materialType;
      const materialOption = checkbox.closest('.material-option');
      const weightInput = materialOption.querySelector('.material-weight-input, .material-count-input');

      if (weightInput && weightInput.value) {
        const isWeightInput = weightInput.classList.contains('material-weight-input');
        selectedMaterials.push({
          type: materialType,
          value: parseFloat(weightInput.value),
          unit: isWeightInput ? 'kg' : 'adet'
        });
      }
    });

    // Build materials list HTML
    const materialsListHTML = selectedMaterials.map(m =>
      `<div style="padding: 0.5rem; background: white; border-radius: 4px; margin-bottom: 0.5rem;">
        <strong>${m.type}:</strong> ${m.value} ${m.unit}
      </div>`
    ).join('');

    // Package info
    const desi = document.getElementById('desi-value').textContent;
    const dimensionalDesi = document.getElementById('dimensional-desi').textContent;
    const weightDesi = document.getElementById('weight-desi').textContent;
    const totalWeight = this.totalWeight || 0;

    const hasMakara = document.querySelector('.material-checkbox[data-material-type="Makara"]:checked');
    const hasWeightMaterial = document.querySelector('.material-checkbox[data-material-type="PLA"]:checked, .material-checkbox[data-material-type="PETG"]:checked');
    const onlyMakara = hasMakara && !hasWeightMaterial;

    const dimensions = onlyMakara ? 'Makara' : `${formData.get('package_length')}×${formData.get('package_width')}×${formData.get('package_height')} cm`;

    let packageHTML = `
      <div style="margin-bottom: 1rem;">
        <strong style="display: block; margin-bottom: 0.5rem;">İade Edilecek Materyaller:</strong>
        ${materialsListHTML}
      </div>
      <div><strong>Toplam Ağırlık:</strong> ${totalWeight.toFixed(2)} kg</div>
      <div><strong>Boyutlar:</strong> ${dimensions}</div>
    `;

    if (!onlyMakara) {
      packageHTML += `<div><strong>Desi:</strong> ${desi}</div>`;
    }

    if (formData.get('package_notes')) {
      packageHTML += `<div style="margin-top: 0.75rem;"><strong>Notlar:</strong> ${formData.get('package_notes')}</div>`;
    }
    document.getElementById('summary-package').innerHTML = packageHTML;
  }

  async handleSubmit() {
    // Idempotency guard - prevent double submissions
    if (this.isSubmitting) {
      console.warn('⏸️ Zaten gönderim yapılıyor, duplicate submit engellendi');
      return;
    }
    this.isSubmitting = true;

    // Check terms acceptance
    const termsAccept = document.getElementById('terms-accept');
    if (!termsAccept.checked) {
      this.isSubmitting = false;
      this.showError('Onay gerekli', 'Lütfen iade gönderim şartlarını okuyup kabul edin.');
      return;
    }

    // Pre-submit cooldown recheck - sadece Sheets net cevap verirse blokla
    if (!window.theme?.is_developer) {
      try {
        const tempChecker = Object.create(ReturnsCooldownChecker.prototype);
        tempChecker.cooldownDays = 30;
        const lastDate = await tempChecker.getLastShipmentDate();
        if (lastDate) {
          const days = (Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24);
          if (days < 30) {
            this.isSubmitting = false;
            const next = new Date(lastDate);
            next.setDate(next.getDate() + 30);
            this.showError('Bekleme süresi aktif', `Son gönderinizden bu yana 30 gün geçmemiş. Sonraki gönderi tarihi: ${next.toLocaleDateString('tr-TR')}`);
            return;
          }
        }
      } catch (err) {
        console.warn('Pre-submit cooldown check error, devam ediliyor:', err);
      }
    }

    // Collect form data
    const formData = new FormData(this.form);
    const customerId = this.form.dataset.customerId;

    // Collect selected materials
    const selectedMaterials = [];
    const checkboxes = document.querySelectorAll('.material-checkbox:checked:not([disabled])');

    checkboxes.forEach(checkbox => {
      const materialType = checkbox.dataset.materialType;
      const materialOption = checkbox.closest('.material-option');
      const weightInput = materialOption.querySelector('.material-weight-input, .material-count-input');

      if (weightInput && weightInput.value) {
        const isWeightInput = weightInput.classList.contains('material-weight-input');
        selectedMaterials.push({
          type: materialType,
          value: parseFloat(weightInput.value),
          unit: isWeightInput ? 'kg' : 'adet'
        });
      }
    });

    // Build materials string for notes
    const materialsString = selectedMaterials.map(m => `${m.type}: ${m.value} ${m.unit}`).join(', ');

    const hasMakaraSubmit = document.querySelector('.material-checkbox[data-material-type="Makara"]:checked');
    const hasWeightMaterialSubmit = document.querySelector('.material-checkbox[data-material-type="PLA"]:checked, .material-checkbox[data-material-type="PETG"]:checked');
    const onlyMakaraSubmit = hasMakaraSubmit && !hasWeightMaterialSubmit;

    const shipmentData = {
      sender: {
        name: formData.get('sender_name'),
        phone: formData.get('sender_phone').replace(/\s/g, ''),
        email: formData.get('sender_email'),
        address: formData.get('sender_address'),
        city: formData.get('sender_city'),
        district: formData.get('sender_district'),
        postal_code: formData.get('sender_postal')
      },
      package: {
        weight: this.totalWeight || 0,
        length: onlyMakaraSubmit ? 0 : (parseInt(formData.get('package_length')) || 0),
        width: onlyMakaraSubmit ? 0 : (parseInt(formData.get('package_width')) || 0),
        height: onlyMakaraSubmit ? 0 : (parseInt(formData.get('package_height')) || 0),
        materials: selectedMaterials,
        notes: formData.get('package_notes') || '',
        desi: onlyMakaraSubmit ? this.totalWeight : parseFloat(document.getElementById('desi-value').textContent),
        dimensional_desi: onlyMakaraSubmit ? 0 : parseFloat(document.getElementById('dimensional-desi').textContent),
        weight_desi: onlyMakaraSubmit ? this.totalWeight : parseFloat(document.getElementById('weight-desi').textContent),
        only_makara: onlyMakaraSubmit
      },
      customer_id: customerId
    };

    // Show loading
    this.showLoading('Kargo etiketi oluşturuluyor...');

    try {
      // Call API
      const result = await this.createShippingLabel(shipmentData);

      // Hide loading
      this.hideLoading();

      if (result.success) {
        // Save to localStorage
        this.saveToHistory(result.data);

        // Show success page
        this.showSuccess(result.data);

        // Clear saved form data
        this.clearSavedData();
      } else {
        this.showError('Kargo etiketi oluşturulamadı', result.error || 'Beklenmeyen bir hata oluştu. Lütfen bilgilerinizi kontrol edin.');
      }
    } catch (error) {
      this.hideLoading();
      console.error('Submit error:', error);
      this.showError('Bağlantı hatası', 'Bir hata oluştu. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
    } finally {
      this.isSubmitting = false;
    }
  }

  async createShippingLabel(data) {
    // Call Navlungo API
    try {
      // Use proxy endpoint to avoid CORS
      const proxyEndpoint = window.theme?.settings?.navlungo_proxy_endpoint || '';
      const carrierId = parseInt(window.theme?.settings?.navlungo_carrier_id) || null;
      const recipientAddressId = parseInt(window.theme?.settings?.navlungo_recipient_address_id) || null;

      // Validate required settings
      if (!proxyEndpoint) {
        return {
          success: false,
          error: 'Proxy endpoint yapılandırılmamış. Lütfen tema ayarlarından proxy endpoint ekleyin.'
        };
      }

      if (!carrierId) {
        return {
          success: false,
          error: 'Kargo firması ID eksik. Lütfen tema ayarlarından ekleyin. (Örnek: Sürat Kargo: 9, ARAS Kargo: 13)'
        };
      }

      if (!recipientAddressId || recipientAddressId === 'null' || recipientAddressId <= 0) {
        return {
          success: false,
          error: 'ÖNEMLI: Alıcı depo adres ID eksik veya geçersiz. Lütfen tema ayarlarından ekleyin. (Örnek: 150, 399 gibi geçerli bir sayı olmalı)'
        };
      }

      // Format phone number to Navlungo format: +90 5XX XXX XX XX
      const formatPhone = (phone) => {
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.startsWith('90')) {
          const national = cleaned.substring(2);
          return `+90 ${national.substring(0, 3)} ${national.substring(3, 6)} ${national.substring(6, 8)} ${national.substring(8, 10)}`;
        } else if (cleaned.startsWith('0')) {
          return `+90 ${cleaned.substring(1, 4)} ${cleaned.substring(4, 7)} ${cleaned.substring(7, 9)} ${cleaned.substring(9, 11)}`;
        }
        return phone;
      };

      // Debug: Log configuration
      console.log('=== İADE GÖNDERİMİ PAYLOAD HAZIRLANIYOR ===');
      console.log('Carrier ID:', carrierId);
      console.log('Depo Address ID:', recipientAddressId);
      console.log('Müşteri Bilgileri:', data.sender);
      console.log('✅ Navlungo Docs: post_type: 3, endpoint: /v2.1/post/return');

      // Prepare payload according to Navlungo API documentation
      // İADE SENARYOSU (Navlungo Official Docs - post/return endpoint):
      // - sender = MÜŞTERİ (pickup location) → TAM BİLGİLER
      // - recipient = DEPO (warehouse) → SADECE addressId
      // - post_type = 3 (İade Alımı)
      // - endpoint = /v2.1/post/return (özel iade endpoint'i)
      const payload = {
        platform: 'shopify',
        posts: [
          {
            reference_id: 'RETURN_' + Date.now(),
            carrier_id: carrierId, // Örnek: 9=Sürat, 13=ARAS
            post_type: 3, // İade Alımı (Navlungo docs)
            cod_payment_type: '',

            // SENDER = MÜŞTERİ (Paketin Kimden Alınacağı - Tam Bilgiler)
            sender: {
              name: data.sender.name,
              phone: formatPhone(data.sender.phone),
              email: data.sender.email,
              address: data.sender.address,
              country: 'tr',
              city: data.sender.city,
              district: data.sender.district,
              post_code: data.sender.postal_code || ''
            },

            // RECIPIENT = DEPO (Paketin Nereye Gideceği - Sadece addressId)
            recipient: {
              addressId: String(recipientAddressId) // String olarak gönder (API requirement test)
            },
            post: {
              desi: data.package.desi,
              package_count: 1,
              price: '',
              note: `Materyaller: ${data.package.materials.map(m => `${m.type} ${m.value} ${m.unit}`).join(', ')} | Toplam: ${data.package.weight} kg${data.package.only_makara ? '' : ` | Boyut: ${data.package.length}×${data.package.width}×${data.package.height} cm`} | Desi: ${data.package.desi}${data.package.only_makara ? '' : ` (Boyutsal: ${data.package.dimensional_desi}, Ağırlık: ${data.package.weight_desi})`}${data.package.notes ? ' | Notlar: ' + data.package.notes : ''}`
            },
            barcode_format: '',
            custom_data_1: data.package.materials.map(m => m.type).join('+'),
            custom_data_2: data.package.only_makara ? 'Makara' : `${data.package.length}×${data.package.width}×${data.package.height}`,
            custom_data_3: `${data.package.weight}kg`,
            custom_data_4: `Desi:${data.package.desi}`
          }
        ]
      };

      // Worker'ın server-side cooldown kontrolü için customer_id'yi top-level'a ekle
      payload.customer_id = data.customer_id || window.theme?.customer_id || null;

      console.log('Navlungo API Request (via Proxy):', JSON.stringify(payload, null, 2));

      // GÜVENLİK: Credentials Cloudflare Worker'da saklanıyor
      // Frontend'den sadece payload gönderiliyor
      const response = await fetch(proxyEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log('=== NAVLUNGO API RESPONSE ===');
      console.log('Status:', response.status, response.statusText);
      console.log('Response:', JSON.stringify(result, null, 2));

      // Check for success - API returns data array
      if (response.ok && result.status && result.data && result.data.length > 0) {
        const shipment = result.data[0]; // Get first shipment

        // ✅ FIX: API döndürüyor "barcode" (barcode_url DEĞİL!)
        const pdfUrl = shipment.barcode_url || shipment.barcode || '';

        console.log('✅ İade gönderimi başarılı! Post Number:', shipment.post_number);
        console.log('📄 API Field "barcode_url":', shipment.barcode_url);
        console.log('📄 API Field "barcode":', shipment.barcode);
        console.log('📄 Final PDF URL:', pdfUrl);
        console.log('📄 Full Shipment Object:', shipment);

        return {
          success: true,
          data: {
            shipment_id: shipment.post_number,
            tracking_number: shipment.post_number,
            tracking_url: shipment.tracking_url || '',
            label_url: pdfUrl,
            reference_id: shipment.reference_id || '',
            created_at: new Date().toISOString(),
            ...data
          }
        };
      } else {
        console.error('❌ API Hatası:', result);

        // Worker server-side cooldown reddi - özel mesaj göster
        if (response.status === 429 && result.reason === 'cooldown_active') {
          return {
            success: false,
            error: result.message || 'Bekleme süresi aktif. 30 gün dolmadan yeni gönderi yapılamaz.',
            cooldown: true,
            next_available: result.next_available || null
          };
        }

        // Worker rapid-fire lock veya geçici Sheets sorunu
        if (response.status === 429 || response.status === 503) {
          return {
            success: false,
            error: result.message || 'Şu anda gönderi yapılamıyor, lütfen birkaç saniye sonra tekrar deneyin.'
          };
        }

        // Detailed error message
        let errorMsg = 'API Hatası: ';

        if (result.message) {
          errorMsg += result.message;
        }

        // Check for validation errors
        if (result.error && typeof result.error === 'object') {
          errorMsg += '\n\nDetaylar:\n';
          Object.keys(result.error).forEach(key => {
            const errors = result.error[key];
            if (Array.isArray(errors)) {
              errorMsg += `• ${key}: ${errors.join(', ')}\n`;
            } else {
              errorMsg += `• ${key}: ${errors}\n`;
            }
          });
        }

        if (!result.message && result.error && typeof result.error === 'string') {
          errorMsg += result.error;
        }

        return {
          success: false,
          error: errorMsg || 'Bilinmeyen API hatası'
        };
      }
    } catch (error) {
      console.error('API Error:', error);
      return {
        success: false,
        error: 'Bağlantı hatası: ' + error.message
      };
    }
  }

  showSuccess(data) {
    // Hide progress bar on success
    const progressBar = document.querySelector('.returns-progress');
    if (progressBar) {
      progressBar.style.display = 'none';
    }

    // Hide all section headings (title, subtitle, description)
    const sectionHeading = document.querySelector('.section-heading');
    if (sectionHeading) {
      sectionHeading.style.display = 'none';
    }

    // Also hide parent container if exists
    const sectionHeadingContainer = document.querySelector('.returns-shipping-wrapper').previousElementSibling;
    if (sectionHeadingContainer && sectionHeadingContainer.classList.contains('section-heading')) {
      sectionHeadingContainer.style.display = 'none';
    }

    // Hide any heading elements directly
    const formContainer = document.querySelector('.returns-shipping-wrapper');
    if (formContainer && formContainer.parentElement) {
      const headings = formContainer.parentElement.querySelectorAll('h1, h2, h3, .section-heading, .richtext, .rte, p, .subtitle, .description, [class*="heading"], [class*="title"], [class*="description"]');
      headings.forEach(heading => {
        // Don't hide elements inside the form itself
        if (!formContainer.contains(heading)) {
          heading.style.display = 'none';
        }
      });
    }

    // Also hide the entire page-width container's heading section
    const pageWidth = document.querySelector('.page-width');
    if (pageWidth) {
      // Find all text content before the form
      const allElements = pageWidth.children;
      for (let element of allElements) {
        if (element.contains(formContainer)) {
          break; // Stop when we reach the form container
        }
        element.style.display = 'none';
      }
    }

    // Update tracking info
    document.getElementById('success-tracking').innerHTML = `
      <div style="background: #f9f9f9; padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
        <strong style="display: block; margin-bottom: 0.5rem;">Takip Numarası:</strong>
        <div style="font-size: 1.5rem; font-weight: bold; font-family: monospace; margin-bottom: 1rem;">${data.tracking_number}</div>
        ${data.tracking_url ? `
          <a href="${data.tracking_url}" target="_blank" style="color: #0066cc; text-decoration: underline;">
            Kargo durumunu takip et →
          </a>
        ` : ''}
      </div>
    `;

    // Update label download button - WITH PDF READY CHECK
    const labelDownloadContainer = document.getElementById('success-label-download');

    // ⚠️ QA WORKAROUND: Eğer PDF URL yoksa tracking URL göster
    if (!data.label_url || data.label_url === '') {
      console.warn('⚠️ PDF URL eksik! QA ortamı sorunu olabilir. Tracking URL gösteriliyor.');
      labelDownloadContainer.innerHTML = `
        <div style="background: #fff3cd; border: 1px solid #ffc107; padding: 1.5rem; border-radius: 8px; margin-bottom: 1rem;">
          <p style="margin: 0 0 1rem 0; font-weight: 600; color: #856404;">
            ⚠️ Etiket URL alınamadı (QA Ortamı Sorunu)
          </p>
          <p style="margin: 0 0 1rem 0; font-size: 0.9rem; color: #856404;">
            Sisteminiz test modunda çalışıyor. Kargo takip numaranız aşağıdadır:
          </p>
          <div style="background: #fff; padding: 1rem; border-radius: 4px; margin-bottom: 1rem; font-family: monospace; font-size: 1.25rem; font-weight: bold; text-align: center;">
            ${data.tracking_number}
          </div>
          ${data.tracking_url ? `
            <a href="${data.tracking_url}" target="_blank" style="display: inline-block; padding: 1rem 2rem; background: #4B4A48; color: #F9F9F9; text-decoration: none; border-radius: 9999px; font-weight: 500;">Kargo Durumunu Takip Et</a>
          ` : ''}
        </div>
      `;
      this.goToStep('success');
      return;
    }

    labelDownloadContainer.innerHTML = `
      <div style="display: inline-block; padding: 1rem 2rem; background: #4B4A48; color: #F9F9F9; border-radius: 9999px; font-weight: 500; margin-bottom: 2rem; opacity: 0.6;">
        <svg style="display: inline-block; width: 20px; height: 20px; margin-right: 8px; animation: spin 1s linear infinite; vertical-align: middle;" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10" stroke-width="3" stroke-opacity="0.25"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-width="3" stroke-linecap="round"></path>
        </svg>
        Etiket hazırlanıyor...
      </div>
      <style>
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
    `;

    // Show success step
    this.goToStep('success');

    // Wait for PDF to be ready, then update button
    this.waitForPdfReady(data.label_url, labelDownloadContainer, data.tracking_number);
  }

  /**
   * Wait for PDF to be ready with retry mechanism
   */
  async waitForPdfReady(pdfUrl, container, trackingNumber) {
    const maxAttempts = 15; // 15 attempts
    const retryDelay = 2000; // 2 seconds between retries
    let attempt = 0;

    const checkAndRetry = async () => {
      attempt++;
      console.log(`📄 PDF Kontrol (${attempt}/${maxAttempts}): ${pdfUrl}`);

      const isReady = await this.checkPdfAvailability(pdfUrl);

      if (isReady) {
        // PDF ready - show download button
        console.log('✅ PDF hazır!');
        container.innerHTML = `
          <a href="${pdfUrl}" target="_blank" style="display: inline-block; padding: 1rem 2rem; background: #4B4A48; color: #F9F9F9; text-decoration: none; border-radius: 9999px; font-weight: 500; margin-bottom: 2rem;">Kargo Etiketini İndir (PDF)</a>
        `;
      } else if (attempt < maxAttempts) {
        // PDF not ready yet - retry after delay
        console.log(`⏳ PDF henüz hazır değil, ${retryDelay/1000}s sonra tekrar deneniyor...`);

        // Update attempt counter in UI
        container.innerHTML = `
          <div style="display: inline-block; padding: 1rem 2rem; background: #4B4A48; color: #F9F9F9; border-radius: 9999px; font-weight: 500; margin-bottom: 2rem; opacity: 0.6;">
            <svg style="display: inline-block; width: 20px; height: 20px; margin-right: 8px; animation: spin 1s linear infinite; vertical-align: middle;" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke-width="3" stroke-opacity="0.25"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-width="3" stroke-linecap="round"></path>
            </svg>
            Etiket hazırlanıyor... (${attempt}/${maxAttempts})
          </div>
        `;

        setTimeout(checkAndRetry, retryDelay);
      } else {
        // Max attempts reached - show manual link
        console.error('❌ PDF maksimum deneme sayısına ulaşıldı');
        container.innerHTML = `
          <div style="background: #fff3cd; border: 1px solid #ffc107; padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
            <p style="margin: 0 0 0.5rem 0; font-weight: 600; color: #856404;">Etiket hazırlanıyor</p>
            <p style="margin: 0 0 1rem 0; font-size: 0.9rem; color: #856404;">
              Etiketiniz hala hazırlanıyor. Lütfen 1-2 dakika bekleyip aşağıdaki linke tıklayın.
            </p>
            <a href="${pdfUrl}" target="_blank" style="display: inline-block; padding: 1rem 2rem; background: #4B4A48; color: #F9F9F9; text-decoration: none; border-radius: 9999px; font-weight: 500;">Etiketi Görüntüle</a>
          </div>
          <p style="font-size: 0.85rem; color: #666; margin-top: 0.5rem;">
            İpucu: Etiket görünmüyorsa sayfayı yenileyip tekrar deneyin ya da
            <a href="/pages/iade-gecmisi" style="color: #0066cc; text-decoration: underline;">İade Geçmişi</a>
            sayfasından takip numaranızı (${trackingNumber}) kullanarak etikete ulaşabilirsiniz.
          </p>
        `;
      }
    };

    // Start checking
    checkAndRetry();
  }

  /**
   * Check if PDF is available (not 404 or error)
   */
  async checkPdfAvailability(pdfUrl) {
    try {
      const response = await fetch(pdfUrl, {
        method: 'HEAD', // Only check headers, don't download
        mode: 'no-cors' // Avoid CORS issues
      });

      // With no-cors, we can't read the status, but we can check if request completed
      // If PDF doesn't exist, request will fail/timeout
      return true; // If we reach here, assume it's available
    } catch (error) {
      // If fetch fails, PDF might not be ready yet
      console.log('PDF kontrol hatası:', error.message);

      // Fallback: Try with a hidden iframe
      return new Promise((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.style.position = 'absolute';
        iframe.style.width = '1px';
        iframe.style.height = '1px';

        let resolved = false;

        // Success handler
        iframe.onload = () => {
          if (!resolved) {
            resolved = true;
            document.body.removeChild(iframe);
            resolve(true);
          }
        };

        // Error handler
        iframe.onerror = () => {
          if (!resolved) {
            resolved = true;
            document.body.removeChild(iframe);
            resolve(false);
          }
        };

        // Timeout after 5 seconds
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            if (iframe.parentElement) {
              document.body.removeChild(iframe);
            }
            resolve(false); // Assume not ready if timeout
          }
        }, 5000);

        document.body.appendChild(iframe);
        iframe.src = pdfUrl;
      });
    }
  }

  showLoading(message) {
    const submitBtn = document.getElementById('create-label-btn');
    submitBtn.style.pointerEvents = 'none';
    submitBtn.textContent = message;
    submitBtn.style.opacity = '0.6';
  }

  hideLoading() {
    const submitBtn = document.getElementById('create-label-btn');
    submitBtn.style.pointerEvents = 'auto';
    submitBtn.textContent = 'Etiket Oluştur';
    submitBtn.style.opacity = '1';
  }

  saveFormData() {
    const formData = new FormData(this.form);
    const data = {};

    for (let [key, value] of formData.entries()) {
      data[key] = value;
    }

    localStorage.setItem('returns_form_draft', JSON.stringify(data));
  }

  loadSavedData() {
    const saved = localStorage.getItem('returns_form_draft');
    if (saved) {
      try {
        const data = JSON.parse(saved);

        // Restore form values (except for pre-filled customer data)
        Object.keys(data).forEach(key => {
          const input = this.form.querySelector(`[name="${key}"]`);
          if (input && !['sender_name', 'sender_email', 'sender_phone'].includes(key)) {
            if (input.type === 'radio') {
              const radio = this.form.querySelector(`[name="${key}"][value="${data[key]}"]`);
              if (radio) radio.checked = true;
            } else {
              input.value = data[key];
            }
          }
        });

        // Recalculate weight and desi after loading saved data
        this.updateTotalWeight();
        this.calculateDesi();
      } catch (e) {
        console.error('Error loading saved data:', e);
      }
    }
  }

  clearSavedData() {
    localStorage.removeItem('returns_form_draft');
  }

  saveToHistory(shipmentData) {
    // Get existing history
    let history = [];
    try {
      history = JSON.parse(localStorage.getItem('returns_shipment_history') || '[]');
    } catch (e) {
      history = [];
    }

    // Add new shipment to beginning
    history.unshift({
      id: shipmentData.shipment_id,
      tracking_number: shipmentData.tracking_number,
      label_url: shipmentData.label_url,
      created_at: shipmentData.created_at,
      materials: shipmentData.package.materials, // Array of materials
      weight: shipmentData.package.weight,
      desi: shipmentData.package.desi,
      status: 'created'
    });

    // Keep only last 50 shipments
    if (history.length > 50) {
      history = history.slice(0, 50);
    }

    // Save to localStorage
    localStorage.setItem('returns_shipment_history', JSON.stringify(history));

    // Save to Google Sheets
    this.saveToGoogleSheets(shipmentData);
  }

  async saveToGoogleSheets(shipmentData) {
    // Save to Google Sheets via Apps Script Web App
    const googleSheetsUrl = window.theme?.settings?.google_sheets_url || '';

    if (!googleSheetsUrl) {
      console.warn('Google Sheets URL not configured. Skipping...');
      return;
    }

    try {
      console.log('📊 Saving to Google Sheets...');

      const payload = {
        timestamp: new Date().toISOString(),
        tracking_number: shipmentData.tracking_number,
        shipment_id: shipmentData.shipment_id,
        reference_id: shipmentData.reference_id || '',

        // Sender info
        sender_name: shipmentData.sender.name,
        sender_phone: shipmentData.sender.phone,
        sender_email: shipmentData.sender.email,
        sender_address: shipmentData.sender.address,
        sender_city: shipmentData.sender.city,
        sender_district: shipmentData.sender.district,
        sender_postal: shipmentData.sender.postal_code,

        // Package info - Multiple materials
        materials: shipmentData.package.materials.map(m => `${m.type}: ${m.value} ${m.unit}`).join(', '),
        package_weight: shipmentData.package.weight,
        package_dimensions: `${shipmentData.package.length}×${shipmentData.package.width}×${shipmentData.package.height}`,
        package_desi: shipmentData.package.desi,
        dimensional_desi: shipmentData.package.dimensional_desi,
        weight_desi: shipmentData.package.weight_desi,
        package_notes: shipmentData.package.notes || '',

        // URLs
        tracking_url: shipmentData.tracking_url || '',
        label_url: shipmentData.label_url || '',

        // Customer ID
        customer_id: shipmentData.customer_id || ''
      };

      const response = await fetch(googleSheetsUrl, {
        method: 'POST',
        mode: 'no-cors', // Google Apps Script requires no-cors
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('✅ Google Sheets save completed');
    } catch (error) {
      console.error('Google Sheets save error:', error);
      // Don't fail the whole process if Google Sheets fails
    }
  }

  // ====================================
  // İADE ŞARTLARI MODAL
  // ====================================

  openTermsModal() {
    const modal = document.getElementById('returns-terms-modal');
    if (!modal) return;

    // Show modal
    modal.hidden = false;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    document.body.classList.add('has-modal-opening');

    // Trigger animation by setting active attribute after a small delay
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.setAttribute('active', '');
        document.body.classList.add('has-modal-open');
        document.body.classList.remove('has-modal-opening');
      });
    });

    // Setup close handlers
    this.setupModalCloseHandlers(modal);
  }

  setupModalCloseHandlers(modal) {
    // Close button - GÜÇLÜ MOBİL DESTEK
    const closeBtn = modal.querySelector('.drawer__close');
    if (closeBtn) {
      const closeHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.closeTermsModal();
        return false;
      };

      // Multiple event types for maximum compatibility
      closeBtn.addEventListener('click', closeHandler, { passive: false, capture: true });
      closeBtn.addEventListener('touchstart', closeHandler, { passive: false, capture: true });
      closeBtn.addEventListener('touchend', closeHandler, { passive: false, capture: true });
      closeBtn.addEventListener('pointerdown', closeHandler, { passive: false, capture: true });

      // Store for cleanup
      modal._closeHandler = closeHandler;
      modal._closeBtn = closeBtn;
    }

    // Overlay click
    const overlay = modal.querySelector('.overlay');
    if (overlay) {
      const overlayHandler = (e) => {
        if (e.target === overlay) {
          this.closeTermsModal();
        }
      };
      overlay.addEventListener('click', overlayHandler);
      overlay.addEventListener('touchend', overlayHandler, { passive: false });

      // Store for cleanup
      modal._overlayHandler = overlayHandler;
      modal._overlay = overlay;
    }

    // ESC key
    const escHandler = (e) => {
      if (e.key === 'Escape') {
        this.closeTermsModal();
      }
    };
    document.addEventListener('keydown', escHandler);

    // Store handler for cleanup
    modal._escHandler = escHandler;
  }

  closeTermsModal() {
    const modal = document.getElementById('returns-terms-modal');
    if (!modal) return;

    // Remove active state
    modal.removeAttribute('active');

    // Clean up ALL event handlers
    if (modal._escHandler) {
      document.removeEventListener('keydown', modal._escHandler);
      modal._escHandler = null;
    }

    if (modal._closeHandler && modal._closeBtn) {
      modal._closeBtn.removeEventListener('click', modal._closeHandler);
      modal._closeBtn.removeEventListener('touchstart', modal._closeHandler);
      modal._closeBtn.removeEventListener('touchend', modal._closeHandler);
      modal._closeBtn.removeEventListener('pointerdown', modal._closeHandler);
      modal._closeHandler = null;
      modal._closeBtn = null;
    }

    if (modal._overlayHandler && modal._overlay) {
      modal._overlay.removeEventListener('click', modal._overlayHandler);
      modal._overlay.removeEventListener('touchend', modal._overlayHandler);
      modal._overlayHandler = null;
      modal._overlay = null;
    }

    // Wait for animation to complete before hiding
    setTimeout(() => {
      modal.hidden = true;
      document.body.classList.remove('has-modal-open');
      document.body.style.overflow = '';
    }, 400); // Match animation duration
  }
}

// ====================================
// 30 GÜN COOLDOWN KONTROLÜ
// ====================================

class ReturnsCooldownChecker {
  constructor() {
    this.cooldownDays = 30;
    this.checkCooldown();
  }

  async checkCooldown() {
    const cooldownCheck = document.getElementById('returns-cooldown-check');
    const cooldownWarning = document.getElementById('returns-cooldown-warning');
    const formContainer = document.getElementById('returns-form-container');

    // Eğer bu elementler yoksa (tag kontrolünden geçemediyse), çık
    if (!cooldownCheck || !cooldownWarning || !formContainer) {
      console.log('Cooldown check: Elements not found (user may not have recycle_eligible tag)');
      return;
    }

    // Developer bypass - cooldown atla
    if (window.theme?.is_developer) {
      console.log('🔧 Developer modu: Cooldown atlanıyor');
      cooldownCheck.style.display = 'none';
      cooldownWarning.style.display = 'none';
      formContainer.style.display = 'block';
      const form = document.getElementById('returns-shipping-form');
      if (form) { new ReturnsShippingForm(form); }
      return;
    }

    try {
      // Son gönderi tarihini al
      const lastShipmentDate = await this.getLastShipmentDate();

      if (lastShipmentDate) {
        const daysSinceLastShipment = this.calculateDaysSince(lastShipmentDate);
        console.log(`📅 Son gönderi: ${lastShipmentDate.toLocaleDateString('tr-TR')}, ${daysSinceLastShipment} gün önce`);

        if (daysSinceLastShipment < this.cooldownDays) {
          // Cooldown süresi içinde - uyarı göster
          const nextAvailableDate = new Date(lastShipmentDate);
          nextAvailableDate.setDate(nextAvailableDate.getDate() + this.cooldownDays);

          const dateValue = document.getElementById('cooldown-date-value');
          if (dateValue) {
            dateValue.textContent = nextAvailableDate.toLocaleDateString('tr-TR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            });
          }

          // Kontrol ve formu gizle, uyarıyı göster
          cooldownCheck.style.display = 'none';
          formContainer.style.display = 'none';
          cooldownWarning.style.display = 'block';

          console.log(`⏳ Cooldown aktif. Sonraki gönderi: ${nextAvailableDate.toLocaleDateString('tr-TR')}`);
          return;
        }
      }

      // Cooldown yok veya süresi geçmiş - formu göster
      cooldownCheck.style.display = 'none';
      cooldownWarning.style.display = 'none';
      formContainer.style.display = 'block';

      console.log('✅ Cooldown kontrolü geçti, form gösteriliyor');

      // Formu başlat
      const form = document.getElementById('returns-shipping-form');
      if (form) {
        new ReturnsShippingForm(form);
      }

    } catch (error) {
      console.error('Cooldown kontrol hatası:', error);
      // Akıllı fallback: localStorage'da kayıt varsa blokla (dönen kullanıcı),
      // yoksa forma izin ver (ilk kullanıcı mağdur olmasın).
      let hasLocalHistory = false;
      let localLastDate = null;
      try {
        const history = JSON.parse(localStorage.getItem('returns_shipment_history') || '[]');
        if (history.length > 0) {
          const d = this._parseDate(history[0].created_at);
          if (d) {
            hasLocalHistory = true;
            localLastDate = d;
          }
        }
      } catch (_) {}

      if (hasLocalHistory && this.calculateDaysSince(localLastDate) < this.cooldownDays) {
        const next = new Date(localLastDate);
        next.setDate(next.getDate() + this.cooldownDays);
        const dateValue = document.getElementById('cooldown-date-value');
        if (dateValue) {
          dateValue.textContent = next.toLocaleDateString('tr-TR', {
            day: 'numeric', month: 'long', year: 'numeric'
          });
        }
        cooldownCheck.style.display = 'none';
        formContainer.style.display = 'none';
        cooldownWarning.style.display = 'block';
      } else {
        cooldownCheck.style.display = 'none';
        cooldownWarning.style.display = 'none';
        formContainer.style.display = 'block';
        const form = document.getElementById('returns-shipping-form');
        if (form) new ReturnsShippingForm(form);
      }
    }
  }

  // Tarih string'ini parse et - ISO veya Türkçe format (DD.MM.YYYY HH:mm:ss)
  _parseDate(str) {
    if (!str) return null;
    if (str instanceof Date) return isNaN(str.getTime()) ? null : str;
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d;
    // Türkçe format fallback: "10.04.2026 15:04:56" veya "10.04.2026"
    const m = /^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/.exec(String(str).trim());
    if (m) {
      const iso = `${m[3]}-${m[2]}-${m[1]}T${m[4] || '00'}:${m[5] || '00'}:${m[6] || '00'}`;
      const parsed = new Date(iso);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  async getLastShipmentDate() {
    // Önce Google Sheets'den dene (otoriter kaynak)
    const googleSheetsReadUrl = window.theme?.settings?.google_sheets_read_url || '';
    const customerId = window.theme?.customer_id || '';
    const customerEmail = window.theme?.customer_email || '';

    if (googleSheetsReadUrl && customerId) {
      try {
        console.log('📊 Google Sheets\'den son gönderi tarihi alınıyor...');
        const params = new URLSearchParams({ customer_id: String(customerId) });
        if (customerEmail) params.set('email', customerEmail);
        // Cache bypass - CDN/browser cache eski veri dönmesin
        params.set('_ts', String(Date.now()));

        const response = await fetch(`${googleSheetsReadUrl}?${params.toString()}`, {
          method: 'GET',
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });

        if (!response.ok) {
          throw new Error(`Sheets read failed with status ${response.status}`);
        }

        const result = await response.json();

        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
          // En yeni created_at'i bul (sıralama varsayımı yapma)
          let latest = null;
          for (const row of result.data) {
            const d = this._parseDate(row.created_at || row.timestamp);
            if (d && (!latest || d.getTime() > latest.getTime())) {
              latest = d;
            }
          }
          if (latest) {
            console.log('📊 Google Sheets\'den en son gönderi:', latest);
            return latest;
          }
        }
        // Sheets erişildi ama kayıt yok - gerçekten yeni müşteri
        console.log('📭 Google Sheets\'de bu müşteriye ait kayıt yok');
        return null;
      } catch (error) {
        // Sheets erişim hatası - fail-closed için exception throw et
        console.error('❌ Google Sheets erişim hatası:', error);
        throw error;
      }
    }

    // Sheets yapılandırılmamış - localStorage fallback (geriye dönük uyum)
    console.warn('⚠️ google_sheets_read_url veya customer_id eksik, localStorage fallback');
    try {
      const history = JSON.parse(localStorage.getItem('returns_shipment_history') || '[]');
      if (history.length > 0) {
        const lastShipment = history[0];
        const d = this._parseDate(lastShipment.created_at);
        if (d) {
          console.log('💾 localStorage\'dan son gönderi bulundu:', d);
          return d;
        }
      }
    } catch (error) {
      console.warn('localStorage hatası:', error);
    }

    console.log('📭 Hiç gönderi bulunamadı');
    return null;
  }

  calculateDaysSince(date) {
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Form sayfasında cooldown kontrolü yap
  if (document.getElementById('returns-cooldown-check')) {
    new ReturnsCooldownChecker();
  } else {
    // Cooldown elementleri yoksa direkt formu başlat (eski davranış)
    const form = document.getElementById('returns-shipping-form');
    if (form) {
      new ReturnsShippingForm(form);
    }
  }
});
